import { clearStashedDataSource } from '@/features/dataSources/dataSourcesSlice';
import { RouteConstants } from '@/features/router/RouteConstants';
import { CreateStepper, useCreateStepper } from '@/lib/Steppers';
import { DataSource, DataSourceIDEnum, SHOPIFY_DATA_SOURCE_TYPE } from '@/models/dataSource';
import { useAppSelector } from '@/reducers';
import { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CREATE_DATA_SOURCE_SHOPIFY, CREATE_DATA_SOURCE_SHOPIFY_STEPS } from './constants';
import { ShopifyCompletedStep, ShopifyConnectAppStep, ShopifySourceDetailsStep } from './steps';

type CreateDataSourceShopifyContentProps = {
  dataSourceId: DataSourceIDEnum;
  modalProps?: {
    onSuccess: (dataSource: DataSource) => void;
    onSuccessAndContinue?: (dataSource: DataSource) => void;
    closeModal: VoidFunction;
  };
};

export const CreateDataSourceShopifyContent: FC<CreateDataSourceShopifyContentProps> = ({ modalProps }) => {
  const dispatch = useDispatch();
  const { steps, currentStepId, currentStepIndex, goToNextStep, goToPreviousStep, completeCurrentStep } = useCreateStepper(CREATE_DATA_SOURCE_SHOPIFY_STEPS);
  const history = useHistory();
  const dataSourceType = SHOPIFY_DATA_SOURCE_TYPE;
  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource);

  useEffect(() => {
    dispatch(clearStashedDataSource());
  }, []);

  const handleNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  const handlePrevStep = () => {
    goToPreviousStep();
  };

  const handleCancel = () => {
    if (modalProps) {
      modalProps.closeModal();
    } else {
      history.push(`${RouteConstants.dataSources}?catalog`);
    }
  };

  const handleCloseAndContinue = () => {
    if (modalProps && dataSource) {
      if (modalProps.onSuccessAndContinue) {
        modalProps.onSuccessAndContinue(dataSource as DataSource);
      } else {
        modalProps.onSuccess(dataSource as DataSource);
      }
    } else {
      history.push(RouteConstants.audiencesCreate);
    }
  };

  const handleCloseOnly = () => {
    if (modalProps && dataSource) {
      modalProps.onSuccess(dataSource as DataSource);
    } else {
      history.push(RouteConstants.dataSources);
    }
  };

  const stepTemplate = () => {
    switch (currentStepId) {
      case CREATE_DATA_SOURCE_SHOPIFY.SOURCE_DETAILS:
        return <ShopifySourceDetailsStep dataSourceType={dataSourceType} onNextStep={handleNextStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_SHOPIFY.CONNECT_APP:
        return <ShopifyConnectAppStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} />;

      case CREATE_DATA_SOURCE_SHOPIFY.COMPLETED:
        return <ShopifyCompletedStep onInit={completeCurrentStep} onNextStep={handleCloseAndContinue} onPrevStep={handleCloseOnly} isModal={!!modalProps} />;

      default:
        return null;
    }
  };

  return (
    <>
      <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />

      {stepTemplate()}
    </>
  );
};
